/**
 * Component: ResultDetailPanel
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R10, SEARCH-SEMANTIC-V1-R05
 *
 * Slide-over panel displayed when a user clicks "View Full Result" on a
 * search result card. Fetches the guideline section detail by
 * GuidelineSectionId and displays the section text alongside all sibling
 * recommendations.
 *
 * Bug #3878: Implements the missing navigation/content display for the
 * View Full Result action across all search modes.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, BookOpen, FileText } from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';
import { useBranding } from '@/contexts';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { getMetaString } from './search-result-utils';
import { SafeHtml } from '@/components/SafeHtml';

interface SectionDetailResult {
  document_id: string;
  title: string;
  content: string;
  score: number;
  metadata: Record<string, unknown>;
}

interface SectionDetailResponse {
  guideline_section_id: number;
  section_name: string;
  section_text: string;
  results: SectionDetailResult[];
  total_count: number;
}

export interface ResultDetailPanelProps {
  /** The GuidelineSectionId to fetch detail for, or null when closed */
  guidelineSectionId: number | null;
  /** Whether the panel is open */
  open: boolean;
  /** Callback when the panel is closed */
  onClose: () => void;
}

export function ResultDetailPanel({
  guidelineSectionId,
  open,
  onClose,
}: ResultDetailPanelProps): React.ReactElement {
  const [data, setData] = useState<SectionDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { branding } = useBranding();

  useEffect(() => {
    if (!open || guidelineSectionId == null) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setData(null);

    api
      .get<SectionDetailResponse>(`/search/section/${guidelineSectionId}`)
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, guidelineSectionId]);

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg md:max-w-xl overflow-y-auto"
        aria-label="Guideline section detail"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="truncate">{data?.section_name || 'Section Detail'}</span>
          </SheetTitle>
          {data && (
            <SheetDescription>
              {data.total_count} recommendation{data.total_count !== 1 ? 's' : ''} in this section
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-12" role="status">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="ml-2 text-sm text-muted-foreground">Loading section detail...</span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Content */}
          {data && !isLoading && (
            <>
              {/* Section text */}
              {data.section_text && (
                <div>
                  <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                    Section Text
                  </h3>
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <SafeHtml
                      html={data.section_text}
                      as="div"
                      className="text-sm text-foreground prose prose-sm max-w-none"
                    />
                  </div>
                </div>
              )}

              {/* Bug #5059: only show the separator + Recommendations heading
                  when there are sibling recommendations to display */}
              {data.section_text && data.results.length > 0 && <Separator />}

              {/* Sibling recommendations */}
              {data.results.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                  Recommendations
                </h3>
                <div className="space-y-3">
                  {data.results.map((result) => {
                    const recStrength = getMetaString(result.metadata, 'RecommendationStrength');
                    const eviStrength = getMetaString(result.metadata, 'EvidenceStrength');
                    const corColor = recStrength
                      ? branding.corColorMappings.find((m) => m.value === recStrength)?.color ?? null
                      : null;
                    const loeColor = eviStrength
                      ? branding.loeColorMappings.find((m) => m.value === eviStrength)?.color ?? null
                      : null;

                    return (
                      <div key={result.document_id} className="rounded-lg border p-3">
                        <div className="flex items-stretch">
                          {/* COR/LOE colour bands */}
                          {(corColor || loeColor) && (
                            <div className="flex shrink-0 gap-0.5 mr-2" aria-hidden="true">
                              {corColor && (
                                <div
                                  className="w-[0.3rem] shrink-0 rounded-l"
                                  style={{ backgroundColor: corColor }}
                                />
                              )}
                              {loeColor && (
                                <div
                                  className={`w-[0.3rem] shrink-0 ${!corColor ? 'rounded-l' : ''}`}
                                  style={{ backgroundColor: loeColor }}
                                />
                              )}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-1.5 mb-1">
                              <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                              <p className="text-sm font-medium">{result.title}</p>
                            </div>
                            <SafeHtml
                              html={result.content}
                              as="div"
                              className="text-sm text-foreground prose prose-sm max-w-none"
                            />
                            {/* COR/LOE labels */}
                            {(recStrength || eviStrength) && (
                              <p className="mt-1.5 text-xs text-muted-foreground">
                                {recStrength && <span>COR: {recStrength}</span>}
                                {recStrength && eviStrength && <span className="mx-1">&middot;</span>}
                                {eviStrength && <span>LOE: {eviStrength}</span>}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
